import Tooltip from 'rc-tooltip'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import reportApiFailure from 'shared/api/reportApiFailure'
import { UserContext } from 'shared/users/contexts/UserContext'
import useCustomerProfile, { CustomerProfileResponseRow } from '../hooks/useCustomerProfile'
import AccessCodeLabel from './AccessCodeLabel'
import ConfirmationModal from '../../../components/ConfirmationModal'
import CustomerProfileModal from '../components/CustomerProfileModal'
import useNctsProfile, { blankNctsProfile, NctsProfileForm } from '../hooks/useNctsProfile'
import NctsProfileModal from '../components/NctsProfileModal'
import { IClassifier, ISelectOption, OfficeRole } from '../../../types/IClassifier'
import ClassifierService from '../../../services/classifier.service'
import { getNameForLocale, sortByCodeLabel } from '../../../helpers'
import mapOfficesToSelectOptions from '../../../helpers/classifier.helper'
import useCountries from '../../../hooks/useCountries'
import CustomsProfileService from './services/customs_profile.service'
import LoadingSpinner from '../../../components/LoadingSpinner'
import { CustomerProfileRequest, CustomerProfileResponse } from '../../../api/models'

interface CustomsProfileProps {
  customerId: number
}

function sortById(a: CustomerProfileResponseRow, b: CustomerProfileResponseRow) {
  if (a.id !== null && b.id !== null) {
    if (a.id < b.id) {
      return -1
    }
    return (a.id > b.id) ? 1 : 0
  }
  return 0
}

function CustomsProfileTable(props: CustomsProfileProps) {
  const {
    customerId,
  } = props
  const {
    customer,
    user,
  } = useContext(UserContext)
  // const { isNctsProfileModalVisible, setNctsProfileModalVisibility, setNctsProfile } = useContext(CustomerContext)
  const [isCustomerProfileModalVisible, setIsCustomerProfileModalVisible] = useState<boolean>(false)

  const {
    t,
    i18n,
  } = useTranslation()

  const [isNctsProfileModalVisible, setIsNctsProfileModalVisible] = useState<boolean>(false)
  const [nctsProfileId, setNctsProfileId] = useState<number | null>(null)
  const [nctsProfile, setNctsProfile] = useState<NctsProfileForm | null>(null)
  const {
    getNctsProfile,
    saveNctsProfile,
    deleteProfile,
  } = useNctsProfile(nctsProfileId)
  const [guaranteeTypes, setGuaranteeTypes] = useState<Array<ISelectOption>>([])
  const [guaranteeOffices, setGuaranteeOffices] = useState<Array<ISelectOption>>([])
  const [customsProfileData, setCustomsProfileData] = useState<Array<CustomerProfileResponseRow>>([])
  const [shareableProfileData, setShareableProfileData] = useState<Array<CustomerProfileResponseRow>>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState<boolean>(false)
  const [customerProfile, setCustomerProfile] = useState<CustomerProfileResponseRow | null>(null)
  const [action, setAction] = useState<'revoke' | 'delete' | 'public' | null>()
  const {
    profiles,
    removeAccessToProfile,
    publicCustomerProfiles,
    postCustomerProfile,
    putCustomerProfile,
  } = useCustomerProfile(customerId, user?.role === 'ADMIN')
  const { countries } = useCountries(i18n.language)

  useEffect(() => {
    const rows = (profiles ?? []).map((item: CustomerProfileResponse) => ({
      ...item,
      id: item.id,
      name: `${item.nctsProfileName}${item.type === 'SHARED' ? ` (${item.customerName})` : ''}`,
    } as CustomerProfileResponseRow))
    rows.sort(sortById)
    setCustomsProfileData(rows)
  }, [profiles])

  useEffect(() => {
    const shareableRows = publicCustomerProfiles
      .filter((item) => profiles.findIndex((row) => row.nctsProfileId === item.nctsProfileId) === -1)
      .map((item: CustomerProfileResponse) => ({
        ...item,
        id: item.id,
        name: `${item.nctsProfileName} (${item.customerName})`,
      } as CustomerProfileResponseRow))
    shareableRows.sort(sortById)

    setShareableProfileData(shareableRows)
  }, [profiles, publicCustomerProfiles])

  useEffect(() => {
    ClassifierService.getClassifier('GUARANTEE_TYPES')
      .then((guaranteeClassifiers: IClassifier[]) => {
        const guaranteeTypesResponse = guaranteeClassifiers
          .sort(sortByCodeLabel)
          .map((item) => ({
            value: item.code,
            label: `${item.codeLabel} - ${getNameForLocale(item, i18n.language)}`,
          }))

        setGuaranteeTypes(guaranteeTypesResponse)
      })

    ClassifierService.getCustomsOfficesByRoles([OfficeRole.OFFICE_ROLE_GUA])
      .then((classifiers: IClassifier[]) => {
        setGuaranteeOffices(mapOfficesToSelectOptions(classifiers, i18n.language))
      })
  }, [])

  useEffect(() => {
    const profileResponse = getNctsProfile.data ?? null
    if (profileResponse !== null) {
      setNctsProfile(
        {
          customerId,
          id: profileResponse.id,
          name: profileResponse.name,
          grn: profileResponse.grn,
          accessCode: profileResponse.accessCode,
          eori: profileResponse.eori,
          companyName: profileResponse.companyName,
          companyPostalCode: profileResponse.companyPostalCode,
          companyStreet: profileResponse.companyStreet,
          companyStreetNumber: profileResponse.companyStreetNumber,
          companyCity: profileResponse.companyCity,
          companyCountry: profileResponse.companyCountry,
          guaranteeType: profileResponse.guaranteeType,
          authorityOffice: profileResponse.authorityOffice,
          authorityCountry: profileResponse.authorityCountry,
          safetyAndSecurityIdentificationNumber: profileResponse.safetyAndSecurityIdentificationNumber,
          safetyAndSecurityCountry: profileResponse.safetyAndSecurityCountry,
          isPublic: profileResponse.isPublic,
        },
      )
    }
  }, [getNctsProfile.data, nctsProfileId, isNctsProfileModalVisible])

  const revokeAccessToProfile = () => {
    if (customerProfile === null || customerProfile.id == null) {
      setIsLoading(false)
      return
    }

    removeAccessToProfile(customerProfile.id)
      .then(() => {
        toast.success(t('messages.delete', { context: 'successful' }))
      })
      .catch((error) => {
        reportApiFailure(error, () => toast.error(t('messages.delete', { context: 'failed' })))
      })
      .finally(() => {
        setIsConfirmationModalOpen(false)
        setIsLoading(false)
      })
  }

  const saveCustomerProfile = (accessCode: string) => {
    if (customerProfile === null) {
      setIsLoading(false)
      return Promise.reject(Error('Missing customer profile'))
    }

    const request: CustomerProfileRequest = {
      accessCode,
      customerId: customerProfile.customerId,
      id: customerProfile.id,
      nctsProfileId: customerProfile.nctsProfileId,
    }

    const updatePromise = (request.id === null ? postCustomerProfile : putCustomerProfile)(request)

    return updatePromise
      .then(() => {
        setIsLoading(false)
        toast.success(t('messages.savingSuccess'))
      })
      .catch((error) => {
        setIsLoading(false)
        reportApiFailure(error, () => toast.error(t('messages.savingFailed')))
      })
  }
  const toggleNctsProfilePublicity = (form: NctsProfileForm) => {
    saveNctsProfile({
      ...form,
      isPublic: !form?.isPublic,
    })
      .then(() => {
        setIsConfirmationModalOpen(false)
        setIsLoading(false)
        setNctsProfileId(null)
        setNctsProfile(null)
        setIsNctsProfileModalVisible(false)
        toast.success(t('messages.savingSuccess'))
      })
      .catch((error) => {
        setIsLoading(false)
        reportApiFailure(error, () => toast.error(t('messages.savingFailed')))
      })
  }

  const deleteNctsProfile = () => {
    if (nctsProfileId === null) {
      return
    }
    setIsLoading(true)

    deleteProfile(nctsProfileId)
      .then(() => {
        setNctsProfileId(null)
        setIsConfirmationModalOpen(false)
        setIsLoading(false)
        toast.success(t('messages.delete', { context: 'successful' }))
      })
      .catch((error) => {
        setIsLoading(false)
        reportApiFailure(error, () => toast.error(t('messages.delete', { context: 'failed' })))
      })
  }

  const confirmAction = () => {
    setIsLoading(true)
    switch (action) {
      case 'delete':
        deleteNctsProfile()
        break
      case 'public':
        if (!getNctsProfile.isFetching && nctsProfile !== null) {
          toggleNctsProfilePublicity(nctsProfile)
        } else {
          setIsLoading(false)
        }
        break
      case 'revoke':
        revokeAccessToProfile()
        break
      default:
        setIsLoading(false)
        toast.error(`unknown action: ${action}`)
        break
    }
  }

  return (
    <>
      <div className="position-relative heading mt-2">
        <h3>
          {t('customers.customsProfile')}
        </h3>
        {
          customerId && (
            <div className="heading-button d-flex align-items-center">
              {(user?.role === 'CUSTOMER_ADMIN' || (user?.role === 'ADMIN' && (Number(customerId) === customer?.id))) && (
                <button
                  type="button"
                  className="btn ms-2 btn-outline-dark btn-sm d-flex align-items-center"
                  onClick={() => {
                    setNctsProfileId(null)
                    setNctsProfile(structuredClone(blankNctsProfile))
                    setIsNctsProfileModalVisible(true)
                  }}
                >
                  <i className="far fa-plus me-1" />
                  <span>{t('buttons.addProfile')}</span>
                </button>
              )}

              {
                user?.role !== 'ADMIN' && !customsProfileData.some((item) => item.type === 'SHARED') && (
                  <button
                    className="btn btn-secondary btn-sm text-primary shadow-sm mx-1"
                    type="button"
                    disabled={customer?.profileStatus === 'REQUESTED_ACCESS'}
                    onClick={() => {
                      CustomsProfileService.requestTardekProfileAccess()
                        .then(() => {
                          toast.success(t('customsProfile.requestSuccess'))
                        })
                        .catch(() => {
                          toast.error(t('common.error'))
                        })
                    }}
                  >
                    {t('buttons.requestAccess')}
                  </button>
                )
              }
            </div>
          )
        }
      </div>
      <div className="table-responsive">

        <table className="table table-sm align-items-center table-hover table-borderless data-table">
          <thead>
            <tr>
              <th key="name">
                <div>
                  {t('common.name', { context: 'capitalized' })}
                </div>
              </th>
              <th key="grn">
                <div>
                  {t('common.customsProfileGrn')}
                </div>
              </th>
              <th key="accessCode">
                <div>
                  {t('customers.accessCode')}
                </div>
              </th>
              <th key="eori">
                <div>
                  {t('declaration.eoriId')}
                </div>
              </th>
              <th key="actions" aria-label="actions" />
            </tr>
          </thead>
          <tbody>
            {customsProfileData.map((customerProfileRow: CustomerProfileResponseRow) => (
              <React.Fragment key={`${customerProfileRow.id}_fragment_${customerProfileRow.nctsProfileGrn}`}>
                <tr
                  className="pe-auto"
                  key={`${customerProfileRow.id}_table_row_${customerProfileRow.nctsProfileGrn}`}
                >
                  <td
                    role="presentation"
                    key={`${customerProfileRow.id}_name_${customerProfileRow.name}`}
                  >
                    {`${customerProfileRow.name}`}
                  </td>
                  <td
                    role="presentation"
                    key={`${customerProfileRow.id}_grn_${customerProfileRow.nctsProfileGrn}`}
                  >
                    {`${customerProfileRow.nctsProfileGrn}`}
                  </td>
                  <td
                    role="presentation"
                    key={`${customerProfileRow.id}_accessCode_${customerProfileRow.accessCode}`}
                  >
                    <AccessCodeLabel code={customerProfileRow.accessCode} />
                  </td>
                  <td
                    role="presentation"
                    key={`${customerProfileRow.id}_eori_${customerProfileRow.nctsProfileEori}`}
                  >
                    {`${customerProfileRow.nctsProfileEori}`}
                  </td>
                  <td
                    role="presentation"
                    key={`${customerProfileRow.id}_actions_${customerProfileRow.customerId}_${customer.id}`}
                  >
                    {customerProfileRow.id && Number(customerId) === customer.id && (
                      <>
                        {
                          customerProfileRow.type !== 'SHARED' && (
                            <Tooltip
                              overlayClassName="tooltip-lg"
                              overlay={<small>{t('tooltips.editDetails')}</small>}
                            >
                              <button
                                type="button"
                                className="btn btn-link p-0 mx-1"
                                onClick={() => {
                                  if (getNctsProfile.isFetching) {
                                    return
                                  }
                                  setIsNctsProfileModalVisible(true)
                                  setNctsProfileId(customerProfileRow.nctsProfileId)
                                  setNctsProfile(null)
                                }}
                              >
                                {
                                  !getNctsProfile.isFetching && (
                                    <i className="fal fa-edit fa-md" />

                                  )
                                }
                                {
                                  getNctsProfile.isFetching && (
                                    <LoadingSpinner />
                                  )
                                }
                              </button>
                            </Tooltip>
                          )
                        }
                        {
                          user?.role === 'ADMIN' && (
                            <Tooltip
                              overlayClassName="tooltip-lg"
                              overlay={<small>{t('customsProfile.public', { context: `${customerProfileRow.isPublic}` })}</small>}
                            >
                              <button
                                className="btn btn-link p-0 mx-1"
                                type="button"
                                onClick={() => {
                                  setNctsProfile(null)
                                  setNctsProfileId(customerProfileRow.nctsProfileId)
                                  setCustomerProfile(customerProfileRow)
                                  setAction('public')
                                  setIsConfirmationModalOpen(!isConfirmationModalOpen)
                                }}
                              >
                                <i className={`fal fa-md ${customerProfileRow.isPublic ? 'fa-ban' : 'fa-check'}`} />
                              </button>
                            </Tooltip>
                          )
                        }
                        {
                          customerProfileRow.type !== 'SHARED' && (
                            <Tooltip
                              overlayClassName="tooltip-lg"
                              overlay={<small>{t('buttons.delete')}</small>}
                            >
                              <button
                                className="btn btn-link p-0 mx-1"
                                type="button"
                                onClick={() => {
                                  setNctsProfileId(customerProfileRow.nctsProfileId)
                                  setAction('delete')
                                  setIsConfirmationModalOpen(!isConfirmationModalOpen)
                                }}
                              >
                                <i className="fal fa-trash" />
                              </button>
                            </Tooltip>
                          )
                        }
                      </>
                    )}
                    {(Number(customerId) !== customer.id && customerProfileRow.type === 'SHARED') && (
                      <>
                        <Tooltip overlayClassName="tooltip-lg" overlay={<small>{t('tooltips.editDetails')}</small>}>
                          <button
                            type="button"
                            className="btn btn-link p-0 mx-1"
                            onClick={() => {
                              setIsCustomerProfileModalVisible(true)
                              setCustomerProfile(customerProfileRow)
                            }}
                          >
                            <i className="fal fa-edit fa-md" />
                          </button>
                        </Tooltip>
                        <Tooltip overlayClassName="tooltip-lg" overlay={<small>{t('customsProfile.revoke')}</small>}>
                          <button
                            className="btn btn-link p-0 mx-1"
                            type="button"
                            onClick={() => {
                              setCustomerProfile(customerProfileRow)
                              setAction('revoke')
                              setIsConfirmationModalOpen(!isConfirmationModalOpen)
                            }}
                          >
                            <i className="fal fa-unlink fa-md" />
                          </button>
                        </Tooltip>
                      </>
                    )}
                  </td>
                </tr>
              </React.Fragment>
            ))}
            <tr>
              <td colSpan={5} className="text-uppercase text-secondary">{t('customsProfile.shareProfile')}</td>
            </tr>
            {shareableProfileData.map((customerProfileRow: CustomerProfileResponseRow, index: number) => (
              <React.Fragment key={customerProfileRow.id}>
                <tr
                  className={`pe-auto ${index === 0 ? 'border-top' : ''}`}
                  key={`id_${customerProfileRow.id}_${customerProfileRow.name}`}
                >
                  <td
                    role="presentation"
                    key={`${customerProfileRow.id}_name_${customerProfileRow.id}`}
                  >
                    {`${customerProfileRow.name}`}
                  </td>
                  <td
                    role="presentation"
                    key={`${customerProfileRow.id}_grn_${customerProfileRow.nctsProfileGrn}`}
                  >
                    {`${customerProfileRow.nctsProfileGrn}`}
                  </td>
                  <td
                    role="presentation"
                    key={`${customerProfileRow.id}_accessCode_${customerProfileRow.accessCode}`}
                  >
                    <AccessCodeLabel code={customerProfileRow.accessCode} />
                  </td>
                  <td
                    role="presentation"
                    key={`${customerProfileRow.id}_eori_${customerProfileRow.nctsProfileEori}`}
                  >
                    {`${customerProfileRow.nctsProfileEori}`}
                  </td>
                  <td
                    role="presentation"
                    key={`${customerProfileRow.id}_actions_${customerProfileRow.customerId}`}
                  >
                    <Tooltip
                      overlayClassName="tooltip-lg"
                      overlay={<small>{t('customsProfile.shareProfile')}</small>}
                    >
                      <button
                        className="btn btn-link p-0 mx-1"
                        type="button"
                        onClick={() => {
                          setIsCustomerProfileModalVisible(true)
                          setCustomerProfile({
                            ...customerProfileRow,
                            id: null,
                            customerId,
                          })
                        }}
                      >
                        <i className="fal fa-share-alt" />
                      </button>
                    </Tooltip>
                  </td>
                </tr>
              </React.Fragment>
            ))}

            <ConfirmationModal
              title={t('common.confirmAction')}
              isVisible={isConfirmationModalOpen}
              isLoading={isLoading}
              messageBody={t('common.confirmAction', { context: 'confirm' })}
              toggleVisibility={() => {
                setAction(null)
                setCustomerProfile(null)
                setNctsProfileId(null)
                setIsConfirmationModalOpen(!isConfirmationModalOpen)
              }}
              onConfirmation={() => {
                confirmAction()
              }}
              onRejection={() => {
                setAction(null)
                setCustomerProfile(null)
                setNctsProfileId(null)
                setIsConfirmationModalOpen(!isConfirmationModalOpen)
              }}
            />
            {
              customerProfile !== null && (
                <CustomerProfileModal
                  isProfileModalVisible={isCustomerProfileModalVisible}
                  setIsProfileModalVisible={setIsCustomerProfileModalVisible}
                  saveCustomerProfile={saveCustomerProfile}
                  nctsProfileAccessCode={customerProfile.accessCode}
                />
              )
            }
          </tbody>
        </table>
      </div>
      {
        nctsProfile !== null && (
          <NctsProfileModal
            isProfileModalVisible={isNctsProfileModalVisible}
            toggleProfileModal={setIsNctsProfileModalVisible}
            nctsProfile={nctsProfile}
            setNctsProfile={setNctsProfile}
            setNctsProfileId={setNctsProfileId}
            isSharing={false}
            guaranteeTypes={guaranteeTypes}
            authorityOffice={guaranteeOffices}
            authorityCountry={countries}
            saveNctsProfile={saveNctsProfile}
          />
        )
      }
    </>
  )
}

export default CustomsProfileTable
